import React from 'react';

import Button from '@uikit/Form/Button/Button';
import QuestionTooltip from '@uikit/QuestionTooltip/QuestionTooltip';

import { formatUsd } from '../../utils';

import './WalletCard.scss';

type Props = {
  availableFunds: number;
  pendingFunds: number;
  inWithdrawalFunds: number;
  isWithdrawOpen: boolean;
  isWithdrawing: boolean;
  hasWithdrawableMethod: boolean;
  hasVisibleMethods: boolean;
  onOpenWithdraw: () => void;
  onCloseWithdraw: () => void;
  children?: React.ReactNode;
};

function WalletCard({
  availableFunds,
  pendingFunds,
  inWithdrawalFunds,
  isWithdrawOpen,
  isWithdrawing,
  hasWithdrawableMethod,
  hasVisibleMethods,
  onOpenWithdraw,
  onCloseWithdraw,
  children,
}: Props): JSX.Element {
  const withdrawButtonLabel = (() => {
    if (availableFunds <= 0) return 'No funds available';
    if (!hasWithdrawableMethod && hasVisibleMethods) return 'Verify payment method first';
    return 'Withdraw funds';
  })();

  const isWithdrawDisabled =
    (!hasWithdrawableMethod && hasVisibleMethods) || availableFunds <= 0 || isWithdrawing;

  return (
    <div className="wallet-card" data-testid="publisher-wallet">
      <div className="wallet-card__header">
        <span>Wallet</span>
        {isWithdrawOpen ? (
          <Button type="ghost" size="s" onClick={onCloseWithdraw} disabled={isWithdrawing}>
            Close
          </Button>
        ) : (
          <Button
            className="wallet-card__withdraw-btn"
            type="blue"
            size="s"
            onClick={onOpenWithdraw}
            disabled={isWithdrawDisabled}
            data-testid="publisher-withdraw"
            leftIcon="downloadInCircle"
            colorIcon="#3424EB"
          >
            {withdrawButtonLabel}
          </Button>
        )}
      </div>
      <div className="wallet-card__body">
        {isWithdrawOpen && children}

        {inWithdrawalFunds > 0 && (
          <div className="wallet-card__row wallet-card__row--with-gap">
            <div className="wallet-card__row-left">
              <div className="wallet-card__row-heading">
                <h4>In withdrawal</h4>
                <QuestionTooltip
                  icon="info"
                  place="top"
                  text="Withdrawals you’ve already requested but not yet completed."
                />
              </div>
              <div className="wallet-card__row-sub">Request already submitted.</div>
            </div>
            <div className="wallet-card__row-right">
              <h2 data-testid="publisher-in-withdrawal">{formatUsd(inWithdrawalFunds)}</h2>
            </div>
          </div>
        )}

        <div className="wallet-card__row">
          <div className="wallet-card__row-left">
            <div className="wallet-card__row-heading">
              <h4>Available funds</h4>
              <QuestionTooltip
                icon="info"
                place="top"
                text="Earned and approved - ready to withdraw."
              />
            </div>
            <div className="wallet-card__row-sub">Ready to withdraw.</div>
          </div>
          <div className="wallet-card__row-right">
            <h2 data-testid="publisher-balance">{formatUsd(availableFunds)}</h2>
          </div>
        </div>

        <div className="wallet-card__row wallet-card__row--with-gap">
          <div className="wallet-card__row-left">
            <div className="wallet-card__row-heading">
              <h4>Pending funds</h4>
              <QuestionTooltip
                icon="info"
                place="top"
                text="Earned but not yet approved. Moves to Available once approved."
              />
            </div>
            <div className="wallet-card__row-sub">Awaiting approval.</div>
          </div>
          <div className="wallet-card__row-right">
            <h2 data-testid="publisher-pending-funds">{formatUsd(pendingFunds)}</h2>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WalletCard;
